import { AppBackendRequestTypes } from './types';
import {
  createLoadingReducer,
  createRequestReducer,
  DispatchedAction,
  RequestState,
} from '@fullstory/redux-util';
import {
  GetNamedElementSettingsRequest,
  GetNamedElementSettingsResponse,
  GetNamedElementDetailViewRequest,
  GetNamedElementDetailViewResponse,
  GetDefinedEventDetailViewResponse,
  GetDefinedEventDetailViewRequest,
  GetDefinedEventSettingsResponse,
  GetAPIEventDetailViewResponse,
  GetAPIEventDetailViewRequest,
  GetPageDetailViewRequest,
  GetPageDetailViewResponse,
  GetPageSettingsResponse,
  GetPropertyDetailViewResponse,
  GetPropertyDetailViewRequest,
  GetPropertySettingsResponse,
} from '@fullstory/proto/fs/services/dx-objects';
import { combineReducers } from 'redux';
import { NamedElementsActions } from '../namedElements';
import { NamedElementsRequestTypes } from '../namedElements/types';
import { DefinedEventsRequestTypes } from '../searchMeta/definedEvents/types';
import { DefinedEventsActions } from '../searchMeta';
import { AppBackendActions } from './actions';
import { PageRequestTypes } from '../pages/types';
import { PageActions } from '../pages/actions';
import { PropertiesRequestTypes } from '../properties/types';
import { PropertiesActions } from '../properties';

const namedElementSettingsRequest = createRequestReducer<GetNamedElementSettingsResponse>()(
  AppBackendRequestTypes.GetNamedElementSettingsRequest,
  AppBackendRequestTypes.GetNamedElementSettingsSuccess,
  AppBackendRequestTypes.GetNamedElementSettingsFailure,
);

type namedElementSettingsActions =
  | NamedElementsActions
  | DispatchedAction<
      AppBackendRequestTypes.GetNamedElementSettingsRequest,
      AppBackendRequestTypes.GetNamedElementSettingsSuccess,
      AppBackendRequestTypes.GetNamedElementSettingsFailure,
      GetNamedElementSettingsRequest,
      GetNamedElementSettingsResponse
    >;

function getDefaultState<T>(data: T): RequestState<T> {
  return {
    lastRequested: -1,
    lastResponded: -1,
    loading: false,
    error: false,
    data,
    safeMessage: null,
  };
}

const namedElementSettings = (
  state = getDefaultState<GetNamedElementSettingsResponse>({
    namedElements: [],
    pageInfo: null,
    sessionQuota: null,
    activeCount: 0,
    activeLimit: 0,
    activeIssuesCount: 0,
  }),
  action: namedElementSettingsActions,
) => {
  switch (action.type) {
    case NamedElementsRequestTypes.ArchiveNamedElementSuccess: {
      const id = action.data.namedElement?.namedElement?.namedElement?.id;
      if (!id) {
        return state;
      }

      if (!state.data) {
        return state;
      }

      const newData: GetNamedElementSettingsResponse = {
        ...state.data,
        namedElements: state.data.namedElements.filter(element => element.id !== id),
      };

      return { ...state, data: newData };
    }
    case AppBackendRequestTypes.GetNamedElementSettingsRequest:
    case AppBackendRequestTypes.GetNamedElementSettingsSuccess:
    case AppBackendRequestTypes.GetNamedElementSettingsFailure:
      return namedElementSettingsRequest(state, action);
    default:
      return state;
  }
};

const namedElementDetailViewRequest = createRequestReducer<GetNamedElementDetailViewResponse>()(
  AppBackendRequestTypes.GetNamedElementDetailViewRequest,
  AppBackendRequestTypes.GetNamedElementDetailViewSuccess,
  AppBackendRequestTypes.GetNamedElementDetailViewFailure,
);

type namedElementDetailViewActions =
  | NamedElementsActions
  | DispatchedAction<
      AppBackendRequestTypes.GetNamedElementDetailViewRequest,
      AppBackendRequestTypes.GetNamedElementDetailViewSuccess,
      AppBackendRequestTypes.GetNamedElementDetailViewFailure,
      GetNamedElementDetailViewRequest,
      GetNamedElementDetailViewResponse
    >;

type apiEventDetailViewActions =
  | AppBackendActions
  | DispatchedAction<
      AppBackendRequestTypes.GetAPIEventDetailViewRequest,
      AppBackendRequestTypes.GetAPIEventDetailViewSuccess,
      AppBackendRequestTypes.GetAPIEventDetailViewFailure,
      GetAPIEventDetailViewRequest,
      GetAPIEventDetailViewResponse
    >;

type definedEventDetailViewActions =
  | DefinedEventsActions
  | DispatchedAction<
      AppBackendRequestTypes.GetDefinedEventDetailViewRequest,
      AppBackendRequestTypes.GetDefinedEventDetailViewSuccess,
      AppBackendRequestTypes.GetDefinedEventDetailViewFailure,
      GetDefinedEventDetailViewRequest,
      GetDefinedEventDetailViewResponse
    >;

type pageDetailViewActions =
  | PageActions
  | DispatchedAction<
      AppBackendRequestTypes.GetPageDetailViewRequest,
      AppBackendRequestTypes.GetPageDetailViewSuccess,
      AppBackendRequestTypes.GetPageDetailViewFailure,
      GetPageDetailViewRequest,
      GetPageDetailViewResponse
    >;

type propertyDetailViewActions =
  | PropertiesActions
  | DispatchedAction<
      AppBackendRequestTypes.GetPropertyDetailViewRequest,
      AppBackendRequestTypes.GetPropertyDetailViewSuccess,
      AppBackendRequestTypes.GetPropertyDetailViewFailure,
      GetPropertyDetailViewRequest,
      GetPropertyDetailViewResponse
    >;

const defaultNamedElementDetailViewState = getDefaultState<GetNamedElementDetailViewResponse>({
  details: null,
  activityMonitoring: null,
  auditHistory: [],
  objectReferences: null,
});

const defaultDefinedEventDetailViewState = getDefaultState<GetDefinedEventDetailViewResponse>({
  details: null,
  activityMonitoring: null,
  objectReferences: null,
});

const defaultApiEventDetailViewState = getDefaultState<GetAPIEventDetailViewResponse>({
  details: null,
  activityMonitoring: null,
  objectReferences: null,
});

const defaultPageDetailViewState = getDefaultState<GetPageDetailViewResponse>({
  details: null,
  activityMonitoring: null,
  objectReferences: null,
});

const defaultPropertyDetailViewState = getDefaultState<GetPropertyDetailViewResponse>({
  details: null,
  activityMonitoring: null,
  objectReferences: null,
});

const namedElementDetailView = (
  state = defaultNamedElementDetailViewState,
  action: namedElementDetailViewActions,
): ReturnType<typeof namedElementDetailViewRequest> => {
  switch (action.type) {
    case NamedElementsRequestTypes.SilenceNamedElementSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: action.data.issueSilence },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case NamedElementsRequestTypes.UnSilenceNamedElementSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: null },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case AppBackendRequestTypes.GetNamedElementDetailViewRequest:
    case AppBackendRequestTypes.GetNamedElementDetailViewSuccess:
    case AppBackendRequestTypes.GetNamedElementDetailViewFailure:
      return namedElementDetailViewRequest({ ...state }, action);
    default:
      return state;
  }
};

const apiEventDetailViewRequest = createRequestReducer<GetAPIEventDetailViewResponse>()(
  AppBackendRequestTypes.GetAPIEventDetailViewRequest,
  AppBackendRequestTypes.GetAPIEventDetailViewSuccess,
  AppBackendRequestTypes.GetAPIEventDetailViewFailure,
);

const definedEventDetailViewRequest = createRequestReducer<GetDefinedEventDetailViewResponse>()(
  AppBackendRequestTypes.GetDefinedEventDetailViewRequest,
  AppBackendRequestTypes.GetDefinedEventDetailViewSuccess,
  AppBackendRequestTypes.GetDefinedEventDetailViewFailure,
);

const pageDetailViewRequest = createRequestReducer<GetPageDetailViewResponse>()(
  AppBackendRequestTypes.GetPageDetailViewRequest,
  AppBackendRequestTypes.GetPageDetailViewSuccess,
  AppBackendRequestTypes.GetPageDetailViewFailure,
);

const propertyDetailViewRequest = createRequestReducer<GetPropertyDetailViewResponse>()(
  AppBackendRequestTypes.GetPropertyDetailViewRequest,
  AppBackendRequestTypes.GetPropertyDetailViewSuccess,
  AppBackendRequestTypes.GetPropertyDetailViewFailure,
);

const definedEventSettings = createRequestReducer<GetDefinedEventSettingsResponse>()(
  AppBackendRequestTypes.GetDefinedEventSettingsRequest,
  AppBackendRequestTypes.GetDefinedEventSettingsSuccess,
  AppBackendRequestTypes.GetDefinedEventSettingsFailure,
);

const pageSettings = createRequestReducer<GetPageSettingsResponse>()(
  AppBackendRequestTypes.GetPageSettingsRequest,
  AppBackendRequestTypes.GetPageSettingsSuccess,
  AppBackendRequestTypes.GetPageSettingsFailure,
);

const propertySettings = createRequestReducer<GetPropertySettingsResponse>()(
  AppBackendRequestTypes.GetPropertySettingsRequest,
  AppBackendRequestTypes.GetPropertySettingsSuccess,
  AppBackendRequestTypes.GetPropertySettingsFailure,
);

const definedEventDetailView = (
  state = defaultDefinedEventDetailViewState,
  action: definedEventDetailViewActions,
): ReturnType<typeof definedEventDetailViewRequest> => {
  switch (action.type) {
    case DefinedEventsRequestTypes.SnoozeDefinedEventSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: action.data.issueSilence },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case DefinedEventsRequestTypes.UnSnoozeDefinedEventSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: null },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case AppBackendRequestTypes.GetDefinedEventDetailViewRequest:
    case AppBackendRequestTypes.GetDefinedEventDetailViewSuccess:
    case AppBackendRequestTypes.GetDefinedEventDetailViewFailure:
      return definedEventDetailViewRequest({ ...state }, action);
    default:
      return state;
  }
};

const apiEventDetailView = (
  state = defaultApiEventDetailViewState,
  action: apiEventDetailViewActions & { sequenceNumber: number },
): ReturnType<typeof apiEventDetailViewRequest> => {
  switch (action.type) {
    case AppBackendRequestTypes.SnoozeAPIEventIssueSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: action.data.issueSilence },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case AppBackendRequestTypes.UnSnoozeAPIEventIssueSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: null },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case AppBackendRequestTypes.GetAPIEventDetailViewRequest:
    case AppBackendRequestTypes.GetAPIEventDetailViewSuccess:
    case AppBackendRequestTypes.GetAPIEventDetailViewFailure:
      return apiEventDetailViewRequest({ ...state }, action);
    default:
      return state;
  }
};

const pageDetailView = (
  state = defaultPageDetailViewState,
  action: pageDetailViewActions,
): ReturnType<typeof pageDetailViewRequest> => {
  switch (action.type) {
    case PageRequestTypes.SilenceIssueSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: action.data.issueSilence },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case PageRequestTypes.UnsilenceIssueSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: null },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case AppBackendRequestTypes.GetPageDetailViewRequest:
    case AppBackendRequestTypes.GetPageDetailViewSuccess:
    case AppBackendRequestTypes.GetPageDetailViewFailure:
      return pageDetailViewRequest({ ...state }, action);
    default:
      return state;
  }
};

const propertyDetailView = (
  state = defaultPropertyDetailViewState,
  action: propertyDetailViewActions,
): ReturnType<typeof propertyDetailViewRequest> => {
  switch (action.type) {
    case PropertiesRequestTypes.SilenceIssueSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: action.data.issueSilence },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case PropertiesRequestTypes.UnSilenceIssueSuccess: {
      const { data } = state;
      if (data && data.details) {
        const updatedData = {
          ...data,
          details: { ...data.details, silence: null },
        };
        return { ...state, data: updatedData };
      }
      return state;
    }
    case AppBackendRequestTypes.GetPropertyDetailViewRequest:
    case AppBackendRequestTypes.GetPropertyDetailViewSuccess:
    case AppBackendRequestTypes.GetPropertyDetailViewFailure:
      return propertyDetailViewRequest({ ...state }, action);
    default:
      return state;
  }
};

const dashboardDetailViewLoading = createLoadingReducer(
  AppBackendRequestTypes.GetDashboardDetailViewRequest,
  AppBackendRequestTypes.GetDashboardDetailViewSuccess,
  AppBackendRequestTypes.GetDashboardDetailViewFailure,
);

export const appBackendReducer = combineReducers<{
  dashboardDetailViewLoading: ReturnType<typeof dashboardDetailViewLoading>;
  definedEventDetailView: ReturnType<typeof definedEventDetailView>;
  definedEventSettings: ReturnType<typeof definedEventSettings>;
  namedElementDetailView: ReturnType<typeof namedElementDetailView>;
  namedElementSettings: ReturnType<typeof namedElementSettings>;
  apiEventDetailView: ReturnType<typeof apiEventDetailView>;
  pageDetailView: ReturnType<typeof pageDetailView>;
  pageSettings: ReturnType<typeof pageSettings>;
  propertyDetailView: ReturnType<typeof propertyDetailView>;
  propertySettings: ReturnType<typeof propertySettings>;
}>({
  dashboardDetailViewLoading,
  definedEventDetailView,
  definedEventSettings,
  namedElementDetailView,
  namedElementSettings,
  apiEventDetailView,
  pageDetailView,
  pageSettings,
  propertyDetailView,
  propertySettings,
});
